import { Stage, Mixer, Entrance, Block } from '.';
import { Corridor } from './Corridor';
import { MouseEvent } from 'react';
import {
    AreaDto,
    IBlockDto,
    ISeatReservationDto,
    IPlanDto,
    AreaDtoVerticalAlign,
    AreaDtoHorizontalAlign,
} from '../../../clients/services';
import { ISeatDisplayInfo, IBlockDisplayInfo } from '../services/plan-info';

export type PlanAreaSelection =
    | { type: 'stage' }
    | { type: 'mixer' }
    | { type: 'entrance'; index: number }
    | { type: 'corridor'; index: number };

interface PlanProps {
    nrOfDesiredSeats: number;
    plan: IPlanDto;
    blockInfos: IBlockDisplayInfo[] | undefined;
    reservations: ISeatReservationDto[];
    ownReservations: ISeatReservationDto[];
    onClickSeat: (seat: ISeatDisplayInfo) => void;
    devMode: boolean;
    onClickBlock?: (block: IBlockDto) => void;
    onContextMenuBlock?: (event: MouseEvent<HTMLTableCellElement>, block: IBlockDto) => void;
    onContextMenuSeat?: (event: MouseEvent<HTMLDivElement>, seat: ISeatDisplayInfo) => void;
    onClickGap?: (gapInfo: { blockId: number; row: number; seatToShift: number }) => void;
    onClickRemovedSeat?: (removedSeatInfo: { blockId: number; row: number; seat: number }) => void;
    onClickRow?: (rowInfo: { blockId: number; row: number }) => void;
    onClickEmptyCell?: (cell: { row: number; column: number }) => void;
    stopInnerClickPropagation?: boolean;
    selectedBlockId?: number;
    selectedRowNr?: number;
    selectedEmptyCell?: { row: number; column: number };
    selectedArea?: PlanAreaSelection;
    onClickArea?: (area: PlanAreaSelection) => void;
    onClickBackground?: () => void;
}

export const Plan = (props: PlanProps) => {
    const getRowStart = (s: AreaDto | undefined) => {
        if (s && s.row && s.row > 1) {
            return s.row;
        }
        return 1;
    };

    const getRowSpan = (s: AreaDto | undefined) => {
        if (s && s.rowSpan && s.rowSpan > 1) {
            return s.rowSpan;
        }
        return 1;
    };

    const getRowEnd = (s: AreaDto | undefined) => {
        return getRowStart(s) + getRowSpan(s) - 1;
    };

    const getColumnStart = (s: AreaDto | undefined) => {
        if (s && s.column && s.column > 1) {
            return s.column;
        }
        return 1;
    };

    const getColumnSpan = (s: AreaDto | undefined) => {
        if (s && s.colSpan && s.colSpan > 1) {
            return s.colSpan;
        }
        return 1;
    };

    const getColumnEnd = (s: AreaDto | undefined) => {
        return getColumnStart(s) + getColumnSpan(s) - 1;
    };

    const getNrOfRows = () => {
        const plan = props.plan;
        let nrRows = getRowEnd(plan.stage);
        plan.entrances?.map((e) => {
            const entranceEnd = getRowStart(e);
            if (entranceEnd > nrRows) {
                nrRows = entranceEnd;
            }
            return true;
        });
        plan.corridors?.map((c) => {
            const corridorEnd = getRowEnd(c);
            if (corridorEnd > nrRows) {
                nrRows = corridorEnd;
            }
            return true;
        });
        plan.blocks?.map((b) => {
            const blockEnd = getRowEnd(b.area);
            if (blockEnd > nrRows) {
                nrRows = blockEnd;
            }
            return true;
        });
        const mixerEnd = getRowEnd(plan.mixer);
        if (mixerEnd > nrRows) {
            nrRows = mixerEnd;
        }
        return nrRows;
    };

    const getNrOfColumns = () => {
        const plan = props.plan;
        let nrColumns = getColumnEnd(plan.stage);
        plan.entrances?.map((e) => {
            const entranceEnd = getColumnStart(e);
            if (entranceEnd > nrColumns) {
                nrColumns = entranceEnd;
            }
            return true;
        });
        plan.corridors?.map((c) => {
            const corridorEnd = getColumnEnd(c);
            if (corridorEnd > nrColumns) {
                nrColumns = corridorEnd;
            }
            return true;
        });
        plan.blocks?.map((b) => {
            const blockEnd = getColumnEnd(b.area);
            if (blockEnd > nrColumns) {
                nrColumns = blockEnd;
            }
            return true;
        });
        const mixerEnd = getColumnEnd(plan.mixer);
        if (mixerEnd > nrColumns) {
            nrColumns = mixerEnd;
        }

        return nrColumns;
    };

    const createRows = () => {
        const numberOfRows = getNrOfRows();
        const numberOfColumns = getNrOfColumns();
        const rows: JSX.Element[] = [];
        for (let rowNr = 1; rowNr <= numberOfRows; rowNr++) {
            const row: JSX.Element[] = [];
            for (let columnNr = 1; columnNr <= numberOfColumns; columnNr++) {
                const block = createBlock(rowNr, columnNr);
                if (block) {
                    row.push(block);
                }
            }
            rows.push(
                <tr className="plan-grid-row" key={'row-' + rowNr}>
                    {row}
                </tr>,
            );
        }
        return rows;
    };

    const startsAt = (s: AreaDto | undefined, rowNr: number, columnNr: number) => {
        return getRowStart(s) === rowNr && getColumnStart(s) === columnNr;
    };

    const getHorizontalAlign = (area: AreaDto | undefined): 'left' | 'right' | 'center' => {
        if (!area) {
            return 'center';
        }
        switch (area.horizontalAlign) {
            case AreaDtoHorizontalAlign.Left:
                return 'left';
            case AreaDtoHorizontalAlign.Right:
                return 'right';
            case AreaDtoHorizontalAlign.Center:
                return 'center';
            default:
                return 'center';
        }
    };

    const getVerticalAlign = (area: AreaDto | undefined): 'top' | 'bottom' | 'middle' => {
        if (!area) {
            return 'middle';
        }
        switch (area.verticalAlign) {
            case AreaDtoVerticalAlign.Top:
                return 'top';
            case AreaDtoVerticalAlign.Bottom:
                return 'bottom';
            case AreaDtoVerticalAlign.Middle:
                return 'middle';
            default:
                return 'middle';
        }
    };

    const getHorizontalDivAlign = (area: AreaDto | undefined): 'left' | 'right' | 'none' => {
        if (!area) {
            return 'none';
        }
        switch (area.horizontalAlign) {
            case AreaDtoHorizontalAlign.Left:
                return 'left';
            case AreaDtoHorizontalAlign.Right:
                return 'right';
            default:
                return 'none';
        }
    };

    const getStyle = (area: AreaDto | undefined) => {
        return {
            textAlign: getHorizontalAlign(area),
            verticalAlign: getVerticalAlign(area),
        };
    };

    const getRotationStyle = (rotation: number | null | undefined) => {
        if (rotation === undefined || rotation === null || Number.isNaN(rotation)) {
            return undefined;
        }
        return {
            transform: `rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            display: 'inline-block',
        } as const;
    };

    const createBlockAreaNr = (area: AreaDto): JSX.Element | undefined => {
        return (
            <div>
                R{area.row}/C{area.column} - RS{getColumnSpan(area)}/CS{getRowSpan(area)}
            </div>
        );
    };

    const createBlock = (rowNr: number, columnNr: number): JSX.Element | undefined => {
        const plan = props.plan;
        if (!plan.stage && rowNr === 1 && columnNr === 1) {
            return (
                <td key={'block:row-' + rowNr + '/column-' + columnNr} className="plan-grid-block stage">
                    <Stage></Stage>
                </td>
            );
        }

        if (plan.stage && startsAt(plan.stage, rowNr, columnNr)) {
            const colSpan = getColumnSpan(plan.stage);
            const rowSpan = getRowSpan(plan.stage);
            const stageIsSelected = props.selectedArea?.type === 'stage';
            return (
                <td
                    key={'block:row-' + rowNr + '/column-' + columnNr}
                    colSpan={colSpan}
                    rowSpan={rowSpan}
                    className="plan-grid-block stage"
                    style={{
                        ...getStyle(plan.stage),
                        outline: stageIsSelected ? '2px solid #1976d2' : 'none',
                        borderRadius: stageIsSelected ? '4px' : undefined,
                        cursor: props.onClickArea ? 'pointer' : undefined,
                    }}
                    onClick={() => props.onClickArea?.({ type: 'stage' })}
                >
                    {props.devMode && createBlockAreaNr(plan.stage)}
                    <div style={getRotationStyle(plan.stage.rotation)}>
                        <Stage
                            width={plan.stage.contentWidth ?? '100%'}
                            align={getHorizontalDivAlign(plan.stage)}
                        ></Stage>
                    </div>
                </td>
            );
        }
        if (plan.stage && isAt(plan.stage, rowNr, columnNr)) {
            return <></>;
        }

        if (plan.mixer && startsAt(plan.mixer, rowNr, columnNr)) {
            const colSpan = getColumnSpan(plan.mixer);
            const rowSpan = getRowSpan(plan.mixer);
            const mixerIsSelected = props.selectedArea?.type === 'mixer';
            return (
                <td
                    key={'block:row-' + rowNr + '/column-' + columnNr}
                    colSpan={colSpan}
                    rowSpan={rowSpan}
                    className="plan-grid-block mixer"
                    style={{
                        ...getStyle(plan.mixer),
                        outline: mixerIsSelected ? '2px solid #1976d2' : 'none',
                        borderRadius: mixerIsSelected ? '4px' : undefined,
                        cursor: props.onClickArea ? 'pointer' : undefined,
                    }}
                    onClick={() => props.onClickArea?.({ type: 'mixer' })}
                >
                    {props.devMode && createBlockAreaNr(plan.mixer)}
                    <div style={getRotationStyle(plan.mixer.rotation)}>
                        <Mixer width={plan.mixer.contentWidth} align={getHorizontalDivAlign(plan.mixer)}></Mixer>
                    </div>
                </td>
            );
        }
        if (plan.mixer && isAt(plan.mixer, rowNr, columnNr)) {
            return undefined;
        }

        const entranceIndex = plan.entrances?.findIndex((e) => startsAt(e, rowNr, columnNr));
        if (entranceIndex !== undefined && entranceIndex >= 0 && plan.entrances) {
            const entrance = plan.entrances[entranceIndex];
            const colSpan = getColumnSpan(entrance);
            const rowSpan = getRowSpan(entrance);
            const entranceIsSelected =
                props.selectedArea?.type === 'entrance' && props.selectedArea.index === entranceIndex;
            return (
                <td
                    key={'block:row-' + rowNr + '/column-' + columnNr}
                    colSpan={colSpan}
                    rowSpan={rowSpan}
                    className="plan-grid-block entrance"
                    style={{
                        ...getStyle(entrance),
                        outline: entranceIsSelected ? '2px solid #1976d2' : 'none',
                        borderRadius: entranceIsSelected ? '4px' : undefined,
                        cursor: props.onClickArea ? 'pointer' : undefined,
                    }}
                    onClick={() => props.onClickArea?.({ type: 'entrance', index: entranceIndex })}
                >
                    {props.devMode && createBlockAreaNr(entrance)}
                    <div style={getRotationStyle(entrance.rotation)}>
                        <Entrance label={entrance.comment}></Entrance>
                    </div>
                </td>
            );
        }
        if (plan.entrances && plan.entrances.some((e) => isAt(e, rowNr, columnNr))) {
            return undefined;
        }

        if (plan.blocks.filter((c) => startsAt(c.area, rowNr, columnNr)).length > 0) {
            const block = plan.blocks.filter((c) => startsAt(c.area, rowNr, columnNr))[0];
            const blockInfo = props.blockInfos?.find((b) => b.blockId === block.id);
            if (!blockInfo) {
                return <></>;
            }
            const colSpan = getColumnSpan(block.area);
            const rowSpan = getRowSpan(block.area);
            const isSelected = props.selectedBlockId === block.id;
            return (
                <td
                    key={'block:row-' + rowNr + '/column-' + columnNr}
                    colSpan={colSpan}
                    rowSpan={rowSpan}
                    className="plan-grid-block seats"
                    style={getStyle(block.area)}
                    onClick={() => props.onClickBlock?.(block)}
                    onContextMenu={(event) => {
                        event.preventDefault();
                        props.onContextMenuBlock?.(event, block);
                    }}
                >
                    <div
                        style={{
                            float: getHorizontalDivAlign(block.area),
                            outline: isSelected ? '2px solid #1976d2' : 'none',
                            borderRadius: '4px',
                            ...getRotationStyle(block.area?.rotation),
                        }}
                    >
                        {props.devMode && block.area && createBlockAreaNr(block.area)}
                        <Block
                            block={block}
                            blockInfo={blockInfo}
                            reservations={props.reservations}
                            ownReservations={props.ownReservations}
                            onClickSeat={props.onClickSeat}
                            devMode={props.devMode}
                            onContextMenuSeat={props.onContextMenuSeat}
                            onClickGap={props.onClickGap}
                            onClickRemovedSeat={props.onClickRemovedSeat}
                            onClickRow={props.onClickRow}
                            stopInnerClickPropagation={props.stopInnerClickPropagation}
                            selectedRow={isSelected ? props.selectedRowNr : undefined}
                            seatRotation={block.seatRotation}
                        ></Block>
                    </div>
                </td>
            );
        }
        if (plan.blocks.filter((b) => isAt(b.area, rowNr, columnNr)).length > 0) {
            return undefined;
        }

        const corridorIndex = plan.corridors?.findIndex((c) => startsAt(c, rowNr, columnNr));
        if (corridorIndex !== undefined && corridorIndex >= 0 && plan.corridors) {
            const corridor = plan.corridors[corridorIndex];
            const colSpan = getColumnSpan(corridor);
            const rowSpan = getRowSpan(corridor);
            const corridorIsSelected =
                props.selectedArea?.type === 'corridor' && props.selectedArea.index === corridorIndex;
            const devStrings = [];
            if (props.devMode) {
                devStrings.push(createBlockAreaNr(corridor));
            }
            return (
                <td
                    key={'block:row-' + rowNr + '/column-' + columnNr}
                    colSpan={colSpan}
                    rowSpan={rowSpan}
                    className="plan-grid-block corridor"
                    style={{
                        ...getStyle(corridor),
                        outline: corridorIsSelected ? '2px solid #1976d2' : 'none',
                        borderRadius: corridorIsSelected ? '4px' : undefined,
                        cursor: props.onClickArea ? 'pointer' : undefined,
                    }}
                    onClick={() => {
                        if (corridorIndex >= 0) {
                            props.onClickArea?.({ type: 'corridor', index: corridorIndex });
                        }
                    }}
                >
                    {props.devMode && <>{devStrings}</>}
                    <div style={getRotationStyle(corridor.rotation)}>
                        <Corridor></Corridor>
                    </div>
                </td>
            );
        }
        if (plan.corridors && plan.corridors.some((c) => isAt(c, rowNr, columnNr))) {
            return undefined;
        }
        const isSelectedEmptyCell =
            props.selectedEmptyCell?.row === rowNr && props.selectedEmptyCell?.column === columnNr;
        return (
            <td
                key={'block:row-' + rowNr + '/column-' + columnNr}
                className={`plan-grid-block empty ${props.onClickEmptyCell ? 'selectable' : ''} ${
                    isSelectedEmptyCell ? 'selected-empty' : ''
                }`}
                style={{ cursor: props.onClickEmptyCell ? 'pointer' : undefined }}
                onClick={() => props.onClickEmptyCell?.({ row: rowNr, column: columnNr })}
            ></td>
        );
    };

    const isAt = (s: AreaDto | undefined, rowNr: number, columnNr: number) => {
        return (
            getRowStart(s) <= rowNr &&
            getRowEnd(s) >= rowNr &&
            getColumnStart(s) <= columnNr &&
            getColumnEnd(s) >= columnNr
        );
    };

    const rows = createRows();
    return (
        <div
            className={'plan-container' + (props.devMode ? ' dev' : '')}
            style={{ marginTop: '40px' }}
            onClick={(event) => {
                if (event.target === event.currentTarget) {
                    props.onClickBackground?.();
                }
            }}
        >
            <table className={'plan-grid'}>
                <tbody>{rows}</tbody>
            </table>
        </div>
    );
};
